import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useLocation } from "react-router-dom";

import { POST_URL, PUT_URL } from "Includes/urls";
import { postRequest, putRequest } from "Includes/api/apicall";
import Swal from "sweetalert2";

const ERPFeaturesDigitalMarketingFeaturesAdd = () => {
  const history = useNavigate();
  const location = useLocation();

  const editId = location.state?.detail;
  const editType = location.state?.type || "erp";
  const rowData = location.state?.rowData;

  const isEdit = !!editId;

  const [type, setType] = useState(editType);
  const [loading, setLoading] = useState(false);

  const [features, setFeatures] = useState(() => {
    if (rowData) {
      return [{
        name: rowData.name || "",
        category: rowData.category || "",
        price: rowData.price || "",
        renewal_price: rowData.renewal_price || "",
        calculation_type: rowData.calculation_type || "monthwise",
        terms_and_condition: (Array.isArray(rowData.terms_and_condition) && rowData.terms_and_condition.length > 0)
          ? rowData.terms_and_condition 
          : [""],
      }];
    }
    return [{ name: "", category: "", price: "", renewal_price: "", calculation_type: "monthwise", terms_and_condition: [""] }];
  });

  // 🔹 Add Row
  const handleAdd = () => {
    setFeatures([
      ...features,
      { name: "", category: "", price: "", renewal_price: "", calculation_type: "monthwise", terms_and_condition: [""] },
    ]);
  };

  // 🔹 Remove Row
  const handleRemove = (index) => {
    const updated = [...features];
    updated.splice(index, 1);
    setFeatures(updated);
  };

  // 🔹 Handle Change
  const handleChange = (index, field, value) => {
    const updated = [...features];
    updated[index][field] = value;
    setFeatures(updated);
  };

  const handleTermsLineChange = (featureIndex, termIndex, value) => {
    const updated = [...features];
    const terms = [...(updated[featureIndex].terms_and_condition || [])];
    terms[termIndex] = value;
    updated[featureIndex] = { ...updated[featureIndex], terms_and_condition: terms };
    setFeatures(updated);
  };

  const addTermsLine = (featureIndex) => {
    const updated = [...features];
    const terms = [...(updated[featureIndex].terms_and_condition || []), ""];
    updated[featureIndex] = { ...updated[featureIndex], terms_and_condition: terms };
    setFeatures(updated);
  };

  const removeTermsLine = (featureIndex, termIndex) => {
    const updated = [...features];
    const terms = [...(updated[featureIndex].terms_and_condition || [])];
    terms.splice(termIndex, 1);
    updated[featureIndex] = {
      ...updated[featureIndex],
      terms_and_condition: terms.length ? terms : [""],
    };
    setFeatures(updated);
  };

  // 🔹 Change Type
  const handleTypeChange = (value) => {
    setType(value);

    const resetData =
      value === "erp"
        ? features.map((f) => ({
            name: f.name,
            category: "",
            price: "",
            renewal_price: "",
            calculation_type: "monthwise",
            terms_and_condition: [""],
          }))
        : features.map((f) => ({
            name: f.name,
            category: "",
            price: "",
            renewal_price: "",
            calculation_type: "monthwise",
            terms_and_condition: [""],
          }));

    setFeatures(resetData);
  };

  // 🔥 SUBMIT FUNCTION
  const handleSubmit = async () => {
    try {
      setLoading(true);

      const filtered = features.filter((f) => f.name.trim() !== "");

      if (filtered.length === 0) {
        Swal.fire("Warning", "Please add at least one feature", "warning");
        setLoading(false);
        return;
      }

      let payload = [];

      if (type === "erp") {
        const invalid = filtered.find((f) => !f.category);
        if (invalid) {
          Swal.fire(
            "Warning",
            "Please fill category for all ERP features",
            "warning"
          );
          setLoading(false);
          return;
        }

        payload = filtered.map((f) => ({
          name: f.name,
          category: f.category,
        }));
      } else {
        const invalid = filtered.find((f) => !f.price);
        if (invalid) {
          Swal.fire(
            "Warning",
            "Please fill price for all Digital features",
            "warning"
          );
          setLoading(false);
          return;
        }

        payload = filtered.map((f) => ({
          name: f.name,
          price: Number(f.price),
          calculation_type: f.calculation_type || "monthwise",
          renewal_price: f.renewal_price
            ? Number(f.renewal_price)
            : null,
          terms_and_condition: (Array.isArray(f.terms_and_condition) ? f.terms_and_condition : [])
            .map((t) => String(t || "").trim())
            .filter(Boolean),
        }));
      }

      let res;
      if (isEdit) {
        // Edit logic for single row update
        const url = type === "erp"
            ? `${PUT_URL.erpfeature.api}${editId}/`
            : `${PUT_URL.digitalmarketingfeature.api}${editId}/`;

        // Payload for update is just a single object, not an array
        res = await putRequest(url, payload[0]);
      } else {
        const url = type === "erp"
            ? POST_URL.erpfeature.api
            : POST_URL.digitalmarketingfeature.api;

        res = await postRequest(url, payload);
      }

      // ✅ Success Alert
      await Swal.fire(
        "Success",
        isEdit ? "Feature updated successfully ✅" : "Features added successfully ✅",
        "success"
      );

      setFeatures([{ name: "", category: "", price: "", renewal_price: "", calculation_type: "monthwise", terms_and_condition: [""] }]);

      history(
        "/billing/erp-features-digital-marketing-features/list"
      );
    } catch (err) {
      console.error(err);

      Swal.fire("Error", "Something went wrong ❌", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* 🔹 Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          {isEdit ? "Edit Feature" : "Add Feature"}
        </Typography>
        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: "none", borderRadius: 2, ml: 1 }}
            onClick={() => history(-1)}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2, ml: 1 }}
            onClick={() =>
              history(
                "/billing/erp-features-digital-marketing-features/list"
              )
            }
          >
            View Features
          </Button>
        </Box>
      </Box>

      {/* 🔹 Form */}
      <Paper sx={{ p: 3 }}>
        {/* 🔸 Type */}
        <Box sx={{ mb: 3 }}>
          <TextField
            select
            label="Feature Type"
            sx={{ width: "30%" }}
            value={type}
            onChange={(e) => handleTypeChange(e.target.value)}
            disabled={isEdit}
          >
            <MenuItem value="erp">ERP Feature</MenuItem>
            <MenuItem value="digital">Other Service</MenuItem>
          </TextField>
        </Box>

        {/* 🔸 Rows */}
        {features.map((item, index) => (
          <Paper
            key={index}
            sx={{
              p: 2,
              mb: 2,
              border: "1px solid #eee",
              borderRadius: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Name */}
              <Grid item xs={12} md={5}>
                <TextField
                  label="Name"
                  fullWidth
                  value={item.name}
                  onChange={(e) =>
                    handleChange(index, "name", e.target.value)
                  }
                />
              </Grid>

              {/* ERP */}
              {type === "erp" && (
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Category"
                    fullWidth
                    value={item.category}
                    onChange={(e) =>
                      handleChange(index, "category", e.target.value)
                    }
                  />
                </Grid>
              )}

              {/* Digital */}
              {type === "digital" && (
                <>
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Price"
                    type="number"
                    fullWidth
                    value={item.price}
                    onChange={(e) =>
                      handleChange(index, "price", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    select
                    label="Calculation Type"
                    fullWidth
                    value={item.calculation_type || "monthwise"}
                    onChange={(e) =>
                      handleChange(index, "calculation_type", e.target.value)
                    }
                  >
                    <MenuItem value="yearwise">Year Wise</MenuItem>
                    <MenuItem value="monthwise">Month Wise</MenuItem>
                    <MenuItem value="studentcount">Student Count</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Renewal Price (Optional)"
                    type="number"
                    fullWidth
                    value={item.renewal_price || ""}
                    onChange={(e) =>
                      handleChange(index, "renewal_price", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Terms and conditions (one or more)
                  </Typography>
                  {(item.terms_and_condition || [""]).map((line, ti) => (
                    <Box key={ti} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        label={`Term ${ti + 1}`}
                        value={line}
                        onChange={(e) =>
                          handleTermsLineChange(index, ti, e.target.value)
                        }
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => addTermsLine(index)}
                        title="Add term"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeTermsLine(index, ti)}
                        disabled={(item.terms_and_condition || [""]).length <= 1}
                        title="Remove term"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Grid>
                </>
              )}

              {/* Delete */}
              <Grid item xs={12} md={2}>
                {!isEdit && (
                  <IconButton
                    color="error"
                    onClick={() => handleRemove(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Paper>
        ))}

        {/* 🔹 Add More */}
        {!isEdit && (
          <Button
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{ textTransform: "none", mb:2, borderRadius: 2, border: '1px solid #d6d5d5ff' }}
          >
            Add More
          </Button>
        )}

        {/* 🔹 Submit */}
        <Box>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : isEdit ? (
              "Update Feature"
            ) : (
              "Save Features"
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ERPFeaturesDigitalMarketingFeaturesAdd;
